import React, {
  createContext,
  useState,
  useContext,
  useMemo,
  useEffect,
} from "react";
import createCache from "@emotion/cache";
import rtlPlugin from "stylis-plugin-rtl";
import { prefixer } from "stylis";
import i18n from "i18next";

const DirectionContext = createContext();

const createEmotionCache = (dir) =>
  createCache({
    key: dir === "rtl" ? "muirtl" : "muiltr",
    stylisPlugins: dir === "rtl" ? [prefixer, rtlPlugin] : [],
  });

const getInitialDirection = () => {
  const storedLang = localStorage.getItem("appLanguage");
  return storedLang && storedLang.trim() === "he" ? "rtl" : "ltr";
};

export const DirectionProvider = ({ children }) => {
  const [direction, setDirection] = useState(getInitialDirection());

  const toggleDirection = () => {
    const newDir = direction === "ltr" ? "rtl" : "ltr";
    setDirection(newDir);
  };

  // The direction follows the interface language (COMY1-397): every
  // i18n.changeLanguage — a switch, its revert after a failed save, or the
  // startup language — sets it explicitly (he → rtl, anything else → ltr),
  // so it can never drift out of step the way a blind toggle could.
  useEffect(() => {
    const syncDirection = (lng) =>
      setDirection(lng && lng.trim().startsWith("he") ? "rtl" : "ltr");
    if (i18n.language) syncDirection(i18n.language);
    i18n.on("languageChanged", syncDirection);
    return () => i18n.off("languageChanged", syncDirection);
  }, []);

  const emotionCache = useMemo(
    () => createEmotionCache(direction),
    [direction]
  );

  useEffect(() => {
    document.body.dir = direction;
  }, [direction]);

  return (
    <DirectionContext.Provider
      value={{ direction, setDirection, toggleDirection, emotionCache }}
    >
      {children}
    </DirectionContext.Provider>
  );
};

export const useDirection = () => useContext(DirectionContext);
